<template>
  <div class="pipeline-funnel-page" dir="rtl">
    <div class="page-head">
      <div>
        <h1 class="page-title">משפך 24 שעות</h1>
        <p class="page-subtitle">
          הצינור המלא — מקליטת קורות חיים מסטארגייט ועד סטרונג, ב-24 השעות האחרונות.
          כל שלב מוצג ביחס לשלב הראשון (בסוגריים: 24 השעות הקודמות).
        </p>
      </div>
      <button class="refresh-btn" :disabled="is_loading" @click="fetchFunnel(true)">
        {{ is_loading ? 'טוען...' : 'רענון' }}
      </button>
    </div>

    <!-- conv-svc degraded: some stages have no data at all -->
    <div v-if="funnel && !convAvailable" class="notice notice-warning">
      שירות השיחות אינו זמין כרגע ({{ reasonLabel(funnel.conv_svc && funnel.conv_svc.reason) }}) — שלבי השיחה מוצגים ללא נתון.
      שאר השלבים מעודכנים.
    </div>

    <div v-if="error_text" class="notice notice-error">
      {{ error_text }}
      <button class="notice-retry" @click="fetchFunnel(true)">נסה שוב</button>
    </div>

    <div v-if="is_loading && !funnel" class="loading-wrap">
      <div class="loading"></div>
    </div>

    <template v-else-if="funnel">
      <div class="funnel">
        <div v-for="(stage, index) in funnel.stages" :key="stage.key" class="stage">
          <!-- The head is inert on every stage except the ones with a drilldown
               behind them (pitched offers, BA breakdown). The affordance is
               attached here (and on the bar) rather than as a separate button so
               the whole row reads as one target. -->
          <div
            class="stage-head"
            :class="{ 'stage-head-drillable': isDrillStage(stage) }"
            :role="isDrillStage(stage) ? 'button' : null"
            :tabindex="isDrillStage(stage) ? 0 : null"
            :aria-expanded="isDrillStage(stage) ? String(isDrilldownOpen(stage)) : null"
            :aria-controls="isDrillStage(stage) ? drilldownId(stage) : null"
            @click="toggleDrilldown(stage)"
            @keydown.enter.prevent="toggleDrilldown(stage)"
            @keydown.space.prevent="toggleDrilldown(stage)"
          >
            <span class="stage-index">{{ index + 1 }}</span>
            <span class="stage-label" :title="stage.source">{{ stage.label_he }}</span>
            <span v-if="stage.count === null" class="stage-empty">אין נתון</span>
            <template v-else>
              <!-- The previous period's raw count rides on the tooltip rather
                   than the row: the headline here is the percentage, and stage 1
                   (which has no percentage of its own) would otherwise have
                   nowhere to show its previous figure. -->
              <span class="stage-count" :title="prevCountTitle(stage)">
                {{ formatNumber(stage.count) }}
              </span>
              <!-- "13.3% (11.1%) מהשלב הראשון" — the parenthesised figure is the
                   SAME ratio over the previous equivalent period, so direction is
                   readable without opening anything. Drawn only when the backend
                   published this stage's prev_count AND a previous first stage to
                   divide by; a missing previous period yields no parentheses at
                   all, never a 0% that would read as a collapse. -->
              <span v-if="index > 0 && pctOfFirst(stage) !== null" class="stage-pct">
                {{ pctOfFirst(stage) }}%
                <span v-if="prevPctOfFirst(stage) !== null" class="pct-prev">
                  ({{ prevPctOfFirst(stage) }}%)
                </span>
                מהשלב הראשון
              </span>
            </template>
            <span v-if="isDrillStage(stage)" class="drill-hint">
              {{ isDrilldownOpen(stage) ? 'הסתר פירוט' : 'הצג פירוט' }}
              <i class="drill-chevron">{{ isDrilldownOpen(stage) ? '⌃' : '‹' }}</i>
            </span>
          </div>

          <!-- Any stage the backend split: one bar divided by segment.
               BA verdicts (green/orange/red) and pitched offers (wanted/declined)
               both arrive in the same {key,label_he,count} shape, so this block is
               deliberately key-agnostic — a new split needs a .seg-* colour and
               nothing else. When the segments do not cover the whole stage (an
               offer pitched with no verdict yet) the uncovered tail stays the
               track colour, and the legend names it. -->
          <div
            v-if="stage.segments"
            class="bar-track"
            :class="{ 'bar-track-drillable': isDrillStage(stage) }"
            @click="toggleDrilldown(stage)"
          >
            <div class="bar bar-segmented" :style="{ width: barWidth(stage) + '%' }">
              <div
                v-for="segment in stage.segments"
                :key="segment.key"
                class="bar-segment"
                :class="['seg-' + segment.key, { 'seg-muted': isMutedSegment(segment) }]"
                :style="{ width: segmentWidth(stage, segment) + '%' }"
                :title="segment.label_he + ': ' + segment.count"
              ></div>
            </div>
          </div>
          <!-- A stage with no data draws no bar at all (barWidth is 0) — the
               "אין נתון" label in the head is the whole statement. -->
          <div
            v-else
            class="bar-track"
            :class="{ 'bar-track-drillable': isDrillStage(stage) }"
            @click="toggleDrilldown(stage)"
          >
            <div class="bar" :style="{ width: barWidth(stage) + '%' }"></div>
          </div>

          <div v-if="stage.segments" class="legend">
            <!-- Zero-count buckets stay in the BAR (at width 0) and are dropped
                 from the legend: the bar keeps its vocabulary, the legend keeps
                 its signal. The dot is never hatched — hatching is illegible at
                 9px — so only the text steps down for a muted bucket. -->
            <span
              v-for="segment in legendSegments(stage)"
              :key="segment.key"
              class="legend-item"
              :class="{ 'legend-muted': isMutedSegment(segment) }"
            >
              <i class="legend-dot" :class="'seg-' + segment.key"></i>
              {{ segment.label_he }} {{ formatNumber(segment.count) }}
            </span>
            <!-- The gap between the segments and the stage total, named so the
                 unfilled tail of the bar is never a mystery. -->
            <span v-if="undecided(stage) !== null" class="legend-item legend-muted">
              <i class="legend-dot seg-undecided"></i>
              ללא הכרעה {{ formatNumber(undecided(stage)) }}
            </span>
          </div>

          <!-- Sub-metric badges live next to the stage they describe.
               The accept rate is shown ONLY when the backend published one: the
               un-excluded rate (which counted the candidate's own application
               and auto-accepted twins as wins) is never displayed again, so a
               backend that cannot compute the clean rate yields no badge at
               all rather than a placeholder. -->
          <div v-if="stage.key === 'offers_pitched' && acceptRateVisible" class="badge-row">
            <span class="badge" :title="acceptRate.note">
              {{ acceptRate.label_he }}: <b>{{ acceptRate.percent }}%</b>
              <!-- Same rule as the stage percentages: the previous period's
                   rate, computed by the backend with the SAME exclusions, and
                   absent entirely when the backend withheld it. -->
              <span v-if="acceptRatePrevPercent !== null" class="pct-prev">
                ({{ acceptRatePrevPercent }}%)
              </span>
              <span class="badge-sub">
                ({{ formatNumber(acceptRate.accepted) }} כן / {{ formatNumber(acceptRate.declined) }} לא)
              </span>
            </span>
            <span class="badge-note">{{ acceptRate.note }}</span>
          </div>

          <!-- Strong promotions split by who made them (Stat.created_by) -->
          <div v-if="stage.key === 'strong' && strongSplit" class="badge-row">
            <span class="badge">
              קידום ידני: <b>{{ formatNumber(strongSplit.manual) }}</b>
            </span>
            <span class="badge">
              קידום אוטומטי (BA): <b>{{ formatNumber(strongSplit.ba_auto) }}</b>
            </span>
            <span v-if="strongSplit.legacy_notified_only" class="badge-note">
              ועוד {{ formatNumber(strongSplit.legacy_notified_only) }} קידומים אוטומטיים
              מלפני תיקון הרישום — נספרים לפי הודעת ההחלטה
            </span>
          </div>

          <div v-if="stage.key === 'sent_to_ba' && twinsToBa" class="badge-row">
            <span class="badge" :title="twinsToBa.note">
              {{ twinsToBa.label_he }}: <b>{{ formatNumber(twinsToBa.count) }}</b>
              <span v-if="twinsToBa.of_total" class="badge-sub">
                (מתוך {{ formatNumber(twinsToBa.of_total) }})
              </span>
            </span>
          </div>

          <!-- How many PEOPLE the stage's pitches reached. The stage count counts
               pitches, and one candidate is routinely pitched the job they applied
               to plus probes plus twins, so without this line the bar reads as if
               every pitch were a separate person. Rendered from detail, not parsed
               out of the note, and absent entirely on a backend that does not
               publish it. -->
          <p v-if="uniqueApplicants(stage) !== null" class="stage-note stage-note-figure">
            {{ peopleLabel(stage) }}<b>{{ formatNumber(uniqueApplicants(stage)) }}</b><!--
            --><template v-if="uniqueOffers(stage) !== null"><!--
              Stage 2 only: the candidacies behind the events. The 326-vs-323 gap
              is the whole reason this figure is published beside the event count
              instead of replacing it. The comment fences keep the template's own
              indentation out of the rendered text, so the separator is hair-space
              + · + hair-space and nothing else.
            -->&#8201;·&#8201;{{ UNIQUE_OFFERS_LABEL }}<b>{{ formatNumber(uniqueOffers(stage)) }}</b><!--
            --></template>
          </p>

          <p v-if="partialNote(stage)" class="stage-note stage-note-partial">{{ partialNote(stage) }}</p>
          <p v-if="stageNote(stage)" class="stage-note">{{ stageNote(stage) }}</p>
          <!-- The row-derived split and the counter headline are two queries up
               to a minute apart. The gap is REPORTED; the bars already scale off
               segments_total, so nothing here is silently reconciled. -->
          <p v-if="stage.reconciliation_note" class="stage-note">{{ stage.reconciliation_note }}</p>

          <!-- Drilldown behind the pitched stage: who was pitched what, and what
               they answered. Rendered inline under the stage it explains so the
               number and its contents are never two screens apart. Fetched on
               first open only — the page's own 60s poll does not touch it. -->
          <div
            v-if="isPitchedStage(stage) && isDrilldownOpen(stage)"
            :id="drilldownId(stage)"
            class="drilldown"
          >
            <div class="drilldown-head">
              <span class="drilldown-title">
                פירוט ההצעות
                <span v-if="pitchedTotals" class="drilldown-title-sub">
                  {{ formatNumber(pitchedTotals.applicants) }} מועמדים ·
                  {{ formatNumber(pitchedTotals.offers) }} הצעות
                </span>
              </span>
              <button class="drill-refresh" :disabled="drill_loading" @click="fetchPitched(true)">
                {{ drill_loading ? 'טוען...' : 'רענון' }}
              </button>
            </div>

            <div v-if="drill_loading && !pitched" class="loading-wrap">
              <div class="loading"></div>
            </div>
            <template v-else>
              <div v-if="drill_error" class="notice notice-error">
                {{ drill_error }}
                <button class="notice-retry" @click="fetchPitched(true)">נסה שוב</button>
              </div>
              <!-- available:false means the backend deliberately withheld the
                   list (usually the conversation service is down). Same tone as
                   the page-level conv-svc notice. -->
              <div v-else-if="pitched && !pitched.available" class="notice notice-warning">
                פירוט ההצעות אינו זמין כרגע ({{ reasonLabel(pitched.reason) }}) — נסה שוב מאוחר יותר.
              </div>
              <p v-else-if="!pitchedApplicants.length" class="stage-note">אין הצעות בחלון הזמן</p>

              <!-- ONE trailing v-else closes the error / unavailable / empty
                   chain above. The chips, the empty-filter line and the cards all
                   live INSIDE it: `fetchPitched`'s .catch leaves `this.pitched`
                   holding the previous response, so a chain broken here would
                   render the error notice AND the stale cards AND the chips at
                   the same time. -->
              <template v-else>
                <!-- Chip filter: one chip per bucket, counts published by the
                     server so they can never disagree with the bar above. Hidden
                     when the row set is partial (a filter over a truncated list
                     would be a wrong number, not a smaller one). -->
                <div v-if="bucketChips.length" class="chip-row">
                  <button
                    v-for="chip in bucketChips"
                    :key="chip.key"
                    type="button"
                    class="chip"
                    :aria-pressed="String(bucket_filter === chipBucket(chip))"
                    :disabled="drill_loading"
                    @click="setBucketFilter(chipBucket(chip))"
                  >
                    <i v-if="chipBucket(chip)" class="chip-dot legend-dot" :class="'seg-' + chipBucket(chip)"></i>
                    {{ chip.label }}
                    <span class="chip-count">{{ formatNumber(chip.count) }}</span>
                  </button>
                </div>

                <p v-if="bucketFilterEmpty" class="stage-note">
                  אין הצעות בקטגוריה שנבחרה — הסינון פעיל על "{{ activeChipLabel }}".
                  לחיצה על "הכול" תחזיר את כל ההצעות.
                  <button type="button" class="chip chip-inline" @click="setBucketFilter(null)">הכול</button>
                </p>

                <div v-else class="drill-cards">
                  <div v-for="(row, rowIndex) in pitchedApplicants" :key="applicantKey(row, rowIndex)" class="drill-card">
                    <div class="drill-card-head">
                      <LinkEntity v-if="row.applicant && row.applicant.resource_id" :payload="applicantPayload(row)" />
                      <span v-else class="drill-plain">{{ applicantName(row) }}</span>
                      <span v-if="row.applicant && row.applicant.phone_number" class="drill-phone">
                        {{ row.applicant.phone_number }}
                      </span>
                      <span class="badge badge-count">
                        {{ offersCountLabel(row) }}
                      </span>
                      <!-- The conversation opens in the SAME modal the BA rows
                           use: one full history over the page, instead of a panel
                           unfolding under the card. -->
                      <button
                        v-if="canOpenChat(row)"
                        class="drill-chat-btn"
                        type="button"
                        title="הצגת השיחה המלאה עם הצ'אטבוט"
                        @click="openChatModal(row)"
                      >צ'אט 💬</button>
                      <span v-else class="badge-note">אין שיחה מקושרת</span>
                    </div>

                    <table class="drill-table">
                      <thead>
                        <tr>
                          <th>משרה</th>
                          <th>חברה</th>
                          <th>תגובה</th>
                          <th>BA</th>
                          <th>פרטים</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(offer, offerIndex) in offersOf(row)" :key="offerKey(offer, offerIndex)">
                          <td>
                            <LinkEntity v-if="positionPayload(offer)" :payload="positionPayload(offer)" />
                            <span v-else class="drill-plain">משרה #{{ offer.offer_id }}</span>
                          </td>
                          <td>
                            <LinkEntity v-if="companyPayload(offer)" :payload="companyPayload(offer)" />
                            <span v-else class="drill-plain">—</span>
                          </td>
                          <td>
                            <span class="drill-tag" :class="'drill-tag-' + responseKey(offer)">
                              {{ responseLabel(offer) }}
                            </span>
                            <span v-if="offer.answered_at" class="drill-answered">
                              {{ formatShortStamp(offer.answered_at) }}
                            </span>
                          </td>
                          <td>
                            <!-- The BA verdict travels with its reasoning as a native
                                 tooltip — the cell stays one tag wide, the "why" is a
                                 hover away. ba:null means BA never evaluated this
                                 pitch (no resolved offer, or not sent yet). -->
                            <template v-if="offer.ba">
                              <span
                                class="drill-tag"
                                :class="'drill-tag-ba-' + baKey(offer)"
                                :title="baTagTitle(offer)"
                              >
                                {{ baLabel(offer) }}
                              </span>
                              <span v-if="offer.ba.round" class="drill-answered">סבב {{ offer.ba.round }}</span>
                              <!-- The FAILING criteria, one per line. Green renders
                                   nothing; an offer with no stored breakdown keeps
                                   the legacy paragraph. -->
                              <p
                                v-for="(line, lineIndex) in baLines(offer)"
                                :key="'ba-line-' + lineIndex"
                                class="ba-line"
                              >{{ line }}</p>
                            </template>
                            <span v-else class="drill-plain">—</span>
                          </td>
                          <td>
                            <!-- The honest bucket, same vocabulary as the bar, the
                                 legend and the chips. -->
                            <span v-if="offer.bucket" class="drill-tag" :class="'drill-tag-' + offer.bucket">
                              {{ offer.bucket_label_he }}
                            </span>
                            <span v-if="offer.is_primary" class="badge badge-mini">המשרה שאליה פנה</span>
                            <span v-if="offer.inherited_from_offer" class="badge badge-mini">
                              תאומה (אושרה אוטומטית)
                            </span>
                            <p v-if="offer.rejection_reason" class="drill-reason">
                              סיבת דחייה: {{ offer.rejection_reason }}
                              <span v-if="offer.rejection_category">({{ offer.rejection_category }})</span>
                            </p>
                            <p v-if="offer.condition_note" class="drill-reason">
                              תנאי: {{ offer.condition_note }}
                              <span v-if="offer.condition_category">({{ offer.condition_category }})</span>
                            </p>
                            <span
                              v-if="!offer.is_primary && !offer.inherited_from_offer && !offer.rejection_reason && !offer.condition_note"
                              class="drill-plain"
                            >—</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </template>
          </div>

          <!-- Drilldown behind the BA breakdown stage: the offers behind each
               colour, grouped by verdict. Same shell as the pitched drilldown
               (head + refresh + loading/error/empty), so the two read as one
               pattern. The verdict is stated once per GROUP rather than on every
               row — inside a group it is already known. -->
          <div
            v-if="isBaStage(stage) && isDrilldownOpen(stage)"
            :id="drilldownId(stage)"
            class="drilldown"
          >
            <div class="drilldown-head">
              <span class="drilldown-title">
                פירוט הצעות לפי החלטת BA
                <span v-if="baTotal !== null" class="drilldown-title-sub">
                  {{ formatNumber(baTotal) }} הצעות
                </span>
              </span>
              <button class="drill-refresh" :disabled="ba_loading" @click="fetchBaBreakdown(true)">
                {{ ba_loading ? 'טוען...' : 'רענון' }}
              </button>
            </div>

            <div v-if="ba_loading && !ba_breakdown" class="loading-wrap">
              <div class="loading"></div>
            </div>
            <template v-else>
              <div v-if="ba_error" class="notice notice-error">
                {{ ba_error }}
                <button class="notice-retry" @click="fetchBaBreakdown(true)">נסה שוב</button>
              </div>
              <p v-else-if="!baGroups.length" class="stage-note">אין הצעות בחלון הזמן</p>

              <div v-else class="drill-cards">
                <div v-for="group in baGroups" :key="group.key" class="drill-card">
                  <div class="drill-card-head">
                    <i class="legend-dot" :class="'seg-' + group.key"></i>
                    <span class="drill-group-label">{{ group.label_he }}</span>
                    <span class="badge badge-count">
                      {{ formatNumber(group.count) }} הצעות
                    </span>
                  </div>

                  <p v-if="!baOffersOf(group).length" class="stage-note">אין הצעות בקבוצה זו</p>

                  <table v-else class="drill-table">
                    <thead>
                      <tr>
                        <th>מועמד</th>
                        <th>משרה</th>
                        <th>חברה</th>
                        <th>הצעה</th>
                        <th>סבב</th>
                        <th>נימוק</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(offer, offerIndex) in baOffersOf(group)" :key="baOfferKey(offer, offerIndex)">
                        <td>
                          <LinkEntity
                            v-if="offer.applicant && offer.applicant.resource_id"
                            :payload="applicantPayload(offer)"
                          />
                          <span v-else class="drill-plain">{{ applicantName(offer) }}</span>
                          <span v-if="offer.applicant && offer.applicant.phone_number" class="drill-phone">
                            {{ offer.applicant.phone_number }}
                          </span>
                          <!-- Same modal as the pitched cards above. It only
                               needs ONE conversation id (or the applicant id to
                               look one up) because conv-svc returns the
                               applicant's whole message history for any of
                               their conversations. -->
                          <button
                            v-if="canOpenChat(offer)"
                            class="drill-chat-btn drill-chat-btn-row"
                            type="button"
                            title="הצגת השיחה המלאה עם הצ'אטבוט"
                            @click="openChatModal(offer)"
                          >צ'אט 💬</button>
                        </td>
                        <td>
                          <LinkEntity v-if="positionPayload(offer)" :payload="positionPayload(offer)" />
                          <span v-else class="drill-plain">משרה #{{ offer.offer_id }}</span>
                        </td>
                        <td>
                          <LinkEntity v-if="companyPayload(offer)" :payload="companyPayload(offer)" />
                          <span v-else class="drill-plain">—</span>
                        </td>
                        <!-- The offer's own details, in the same field set the
                             company-offers table shows: the coloured status
                             badge, the offer number, the meta chips, then the
                             message. The message is EDITABLE through the same
                             modal that screen uses; the status badge is a
                             status-change target there and deliberately is NOT
                             one here (this page is an analytics view). -->
                        <td class="drill-offer-cell">
                          <template v-if="offerOf(offer)">
                            <div class="drill-offer-head">
                              <a
                                v-if="offerOf(offer).status"
                                class="status_color_text drill-status"
                                :style="{ 'background-color': statusColor(offerOf(offer).status) }"
                              >{{ offerOf(offer).status.name }}</a>
                              <span v-else class="drill-plain">ללא סטטוס</span>
                              <!-- Offer number, same "הצעה #<id>" form used in
                                   SendingPolicyRuleCard ("מס׳ הצעה" in StargateLog). -->
                              <span class="drill-offer-id">הצעה #{{ offerOf(offer).id }}</span>
                            </div>

                            <div v-if="offerMeta(offer).length" class="drill-offer-meta">
                              <span v-for="chip in offerMeta(offer)" :key="chip" class="badge badge-mini">
                                {{ chip }}
                              </span>
                            </div>

                            <div v-if="offerDates(offer).length" class="drill-offer-dates">
                              <span v-for="line in offerDates(offer)" :key="line">{{ line }}</span>
                            </div>

                            <!-- Editable, through the SAME modal the company
                                 offers screen uses (Message.vue -> MessageForm).
                                 Mirrors Message.vue's markup: the whole block is
                                 the click target, and an empty message shows the
                                 same edit icon instead of blank space. -->
                            <div
                              class="drill-offer-message drill-offer-editable"
                              title="עריכת הודעת ההצעה"
                              @click="openOfferMessageModal(offer)"
                            >
                              <p
                                v-if="offerOf(offer).message"
                                class="custom_message"
                                v-html="$sanitize(visibleMessage(offer, displayMessage(offerOf(offer).message)))"
                              ></p>
                              <img v-else class="btn drill-edit-icon" src="/edit_icon.svg" width="20px">
                              <button
                                v-if="isLongMessage(displayMessage(offerOf(offer).message))"
                                class="drill-more-btn"
                                @click.stop="toggleMessage(offer, 'message')"
                              >
                                {{ isMessageExpanded(offer, 'message') ? 'הצג פחות' : 'הצג הכל' }}
                              </button>
                            </div>

                            <div v-if="offerOf(offer).auto_message" class="drill-offer-message">
                              <p
                                class="custom_message drill-auto-message"
                                v-html="$sanitize(visibleMessage(offer, offerOf(offer).auto_message, 'auto_message'))"
                              ></p>
                              <button
                                v-if="isLongMessage(offerOf(offer).auto_message)"
                                class="drill-more-btn"
                                @click="toggleMessage(offer, 'auto_message')"
                              >
                                {{ isMessageExpanded(offer, 'auto_message') ? 'הצג פחות' : 'הצג הכל' }}
                              </button>
                            </div>
                          </template>
                          <span v-else class="drill-plain">—</span>
                        </td>
                        <td>
                          <span v-if="offer.round" class="badge badge-mini">סבב {{ offer.round }}</span>
                          <span v-if="offer.decided_at" class="drill-answered">
                            {{ formatShortStamp(offer.decided_at) }}
                          </span>
                        </td>
                        <td>
                          <!-- Reasoning is shown only for the non-match groups (orange /
                               red / unknown): the useful question is why an offer was
                               disqualified. A green offer's "why it fits" is noise, so
                               those rows fall through to the "—" placeholder. -->
                          <p
                            v-for="(line, lineIndex) in baLines(offer, group)"
                            :key="'ba-group-line-' + lineIndex"
                            class="ba-line"
                          >{{ line }}</p>
                          <span v-if="!baLines(offer, group).length" class="drill-plain">—</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </div>

          <!-- ── The 8->9 drop-off strip ──────────────────────────────────────
               Un-numbered and dashed: it belongs to the GAP between stage 8 and
               stage 9, not to the stage list. Rendered only for the two variants
               that have something to say — `truncated` and `no_expected` render
               nothing, each already explained by the note above or vacuous. -->
          <template v-if="stage.key === 'offers_pitched' && dropOffVariant">
            <div
              v-if="dropOffVariant === 'clickable'"
              ref="dropoffStrip"
              class="dropoff-strip dropoff-strip-clickable"
              role="button"
              tabindex="0"
              aria-haspopup="dialog"
              @click="openDropOffModal"
              @keydown.enter.prevent="openDropOffModal"
              @keydown.space.prevent="openDropOffModal"
            >
              <span class="dropoff-arrow">↓</span>
              <span class="dropoff-label">{{ dropOff.label_he }}</span>
              <span class="drill-hint">הצג רשימה <i class="drill-chevron">‹</i></span>
              <p v-if="dropOff.note" class="stage-note">{{ dropOff.note }}</p>
              <p v-if="dropOff.footnote" class="stage-note dropoff-footnote">{{ dropOff.footnote }}</p>
            </div>
            <div v-else class="dropoff-strip dropoff-strip-static">
              <span class="dropoff-arrow">↓</span>
              <span class="dropoff-label">{{ dropOffStaticText }}</span>
            </div>
          </template>
        </div>
      </div>

      <p class="stamp">נכון ל-{{ formatStamp(funnel.generated_at) }}</p>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import LinkEntity from '@/components/LinkEntity'
import TimeHelper from '@/custom/classes/time_helper.js'

// The offer-message edit goes through the EXACT mechanism the company offers
// screen uses (components/admin/CompanyOffers.vue -> components/Message.vue ->
// MessageForm in the modals store): same modal component, same store wiring,
// same PUT endpoint. These are the additional_data values CompanyOffers.vue
// declares for its offer_message field, copied verbatim so both screens save the
// same way.
const OFFER_MESSAGE_ADDITIONAL_DATA = {
  endpoint: '/offers/',
  headers: 'admin_headers',
  resource_type: 'Offer'
}

// "משפך 24 שעות" — read-only pipeline view backed by GET /pipeline_funnel.
// The backend owns every label (label_he) and every source decision; this page
// only scales the bars and renders what it is given, so a stage added on the
// server appears here without a frontend change.
const REFRESH_MS = 60000

// The backend joins a stage's note clauses with this separator, which is what
// lets the page render one clause structurally and keep the rest as prose.
const NOTE_SEPARATOR = ' · '

// Must match Intake::PipelineFunnelService::UNIQUE_APPLICANTS_NOTE (minus the
// trailing space), so the clause the figure line already states is not printed a
// second time inside the note. A label change on the server without one here is
// harmless: the note simply keeps the clause and nothing is lost.
const UNIQUE_APPLICANTS_LABEL = 'מועמדים יוניקיים שקיבלו הצעות: '

// stage key -> the label its people line carries. Stage 8 keeps its historical
// wording (and is the one clause stageNote() still strips out of the note);
// every other stage says the neutral thing, and an UNKNOWN stage key that
// publishes unique_applicants falls back to the same neutral label rather than
// rendering nothing — a stage added on the server gets its people line for free.
const PEOPLE_LABELS = {
  applications_created: 'מועמדים יוניקיים: ',
  intake_conversations: 'מועמדים יוניקיים: ',
  intake_completed: 'מועמדים יוניקיים: ',
  sent_to_ba: 'מועמדים יוניקיים: ',
  strong: 'מועמדים יוניקיים: ',
  offers_pitched: UNIQUE_APPLICANTS_LABEL
}
const PEOPLE_LABEL_FALLBACK = 'מועמדים יוניקיים: '
const UNIQUE_OFFERS_LABEL = 'מועמדויות ייחודיות: '

// The server prepends this clause to stage 8's note when conv-svc truncated the
// row set. It is rendered as its OWN amber line (caution tone, no banner) and
// stripped from the prose note, so the other clauses keep their normal ink.
const PARTIAL_NOTE_PREFIX = 'הנתונים חלקיים'

// The muted buckets, as a FALLBACK only: the server publishes a per-segment
// `muted` boolean and that is what the page reads. This list exists so a rebase
// deployed before the flag existed still renders twins and undecided pitches at
// reduced contrast instead of alarming red-adjacent weight.
const MUTED_SEGMENT_KEYS = ['inherited', 'ours_undecided']

// Chip labels. Only "הצעות שהמועמד הגיש בעצמו" may shorten — the "הצענו" pair
// always stays full, because WHO made the offer is the entire point of the
// split. The legend two lines above always shows the full labels.
const CHIP_LABELS = {
  self_applied: 'עצמיות',
  ours_wanted: 'הצענו — מעוניין',
  ours_declined: 'הצענו — דחה',
  inherited: 'תאומות שאושרו אוטומטית',
  ours_undecided: 'ללא הכרעה'
}
const CHIP_ALL_LABEL = 'הכול'
const CHIP_ALL_KEY = 'all'

// Strip copy for the two variants that render as a static, non-clickable line.
// `truncated` and `no_expected` render NOTHING: the first is already explained
// by the partial note above, the second has nothing that could have dropped.
// Technical slugs are the SERVER's vocabulary, not the reader's. Every reason
// the funnel can publish gets a short Hebrew phrase; an unknown slug falls
// through to itself, so a new server reason degrades to "ugly but honest"
// rather than to a blank.
const REASON_LABELS = {
  conv_svc_unreachable: 'אין חיבור לשירות השיחות',
  conv_svc_timeout: 'שירות השיחות לא ענה בזמן',
  conv_svc_breaker_open: 'שירות השיחות מושבת זמנית לאחר כשלים חוזרים',
  conv_svc_request_aborted: 'הבקשה הופסקה אחרי כשל קודם באותה טעינה',
  conv_svc_unexpected_body: 'שירות השיחות החזיר תשובה לא צפויה',
  conversation_service_url_not_configured: 'כתובת שירות השיחות אינה מוגדרת',
  panel_credentials_not_configured: 'פרטי הגישה לשירות השיחות אינם מוגדרים',
  panel_login_failed: 'ההתחברות לשירות השיחות נכשלה',
  rows_missing: 'לא התקבלו שורות עבור הפעילות הזו',
  pitched_rows_invalid: 'התקבלו נתונים לא תקינים משירות השיחות',
  truncated: 'הנתונים חלקיים'
}

const DROPOFF_TOO_MANY_TEXT =
  'נשרו בדרך ל-BA: לא חושב — יותר מ-1,000 הצעות בחלון הזמן, ולכן החישוב הושהה כדי לא להאט את העמוד. ' +
  'צמצום חלון הזמן יאפשר חישוב.'

const DROPOFF_ROWS_MISSING_TEXT =
  'נשרו בדרך ל-BA: אין נתון — לא התקבלו שורות עבור הפעילות הזו משירות השיחות, ולכן אי אפשר לדעת ' +
  'אילו הצעות היו אמורות להגיע ל-BA. אפשר לנסות שוב מאוחר יותר.'

// The stages with a drilldown behind them. Everything drilldown-related is gated
// on these keys so adding a stage on the server cannot accidentally acquire a
// click target with no endpoint behind it.
const PITCHED_STAGE_KEY = 'offers_pitched'
const BA_STAGE_KEY = 'ba_breakdown'
const DRILL_STAGE_KEYS = [PITCHED_STAGE_KEY, BA_STAGE_KEY]

// Response tone matches the bar segments above: wanted is the same green,
// declined the same red, undecided the same grey — one meaning per colour on the
// whole page.
const RESPONSE_LABELS = {
  wanted: 'רצה',
  declined: 'דחה',
  undecided: 'ללא הכרעה'
}

// offer_statuses.color -> the CSS colour the status badge is painted with. This
// is the SAME mapping components/OfferStatus.vue applies (it partitions statuses
// into the green/orange/purple/red/dark_red lists of the offerStatuses Vuex
// module and picks a colour per list), just driven directly off the `color`
// column the backend now ships — so a status badge here and on the company
// offers screen are the same colour. An unknown/absent colour falls back to
// saddlebrown, exactly as OfferStatus.getStatusColor does.
const STATUS_COLORS = {
  green: 'green',
  orange: 'darkorange',
  purple: 'purple',
  red: 'red',
  dark_red: 'saddlebrown'
}
const STATUS_FALLBACK_COLOR = 'saddlebrown'

// Offer text is capped server-side (1000 chars), but even 1000 chars would blow
// up a table row, so anything past this is folded behind "הצג הכל".
const MESSAGE_PREVIEW_CHARS = 140

// BA verdict tags reuse the exact colours of the BA breakdown bar above, so
// "ירוק" means the same thing on the bar and inside the drilldown. A value
// outside the vocabulary renders as לא ידוע rather than an unstyled tag.
const BA_LABELS = {
  green: 'ירוק',
  orange: 'כתום',
  red: 'אדום',
  unknown: 'לא ידוע'
}

export default {
  name: 'pipeline-funnel-page',
  components: {
    LinkEntity
  },
  data () {
    return {
      funnel: null,
      is_loading: false,
      error_text: '',
      timer: null,
      UNIQUE_APPLICANTS_LABEL: UNIQUE_APPLICANTS_LABEL,
      UNIQUE_OFFERS_LABEL: UNIQUE_OFFERS_LABEL,
      // Which bucket the pitched drilldown is filtered to; null = הכול.
      bucket_filter: null,
      // Which drilldown is open, by stage key — one at a time, so opening the BA
      // list collapses the pitched one instead of stacking two long tables.
      open_drilldown: null,
      // Drilldown state. `pitched` holds the whole response (including
      // available:false) so the reason the backend gave can be shown verbatim.
      pitched: null,
      drill_loading: false,
      drill_error: '',
      // BA drilldown. Purely rebase-side on the server, so it has no
      // available:false branch — it either answers or it errors.
      ba_breakdown: null,
      ba_loading: false,
      ba_error: '',
      // Which offer texts the user unfolded, keyed '<offer_id>:<field>'. Reset
      // whenever the list is refetched so a stale key cannot unfold a different
      // offer that happens to reuse the id.
      expanded_messages: {},
      time_helper: new TimeHelper()
    }
  },
  computed: {
    convAvailable () {
      return !!(this.funnel && this.funnel.conv_svc && this.funnel.conv_svc.available)
    },
    acceptRate () {
      return this.funnel && this.funnel.sub_metrics
        ? this.funnel.sub_metrics.offer_accept_rate
        : null
    },
    // available:false means the backend deliberately withheld the rate (the
    // conversation service is down, or is an older build that cannot exclude the
    // candidate's own offer and auto-accepted twins). Nothing is rendered then —
    // an approximate accept rate is worse than none.
    acceptRateVisible () {
      const rate = this.acceptRate
      return !!(rate && rate.available !== false && rate.percent !== null && rate.percent !== undefined)
    },
    // The previous period's accept rate, or null when there is nothing honest to
    // show. Withheld for the SAME reasons as the current one (conv-svc down, or
    // too old to answer for a shifted window), and the backend says so with
    // prev.available === false — which must not be confused with a real 0%.
    acceptRatePrevPercent () {
      const rate = this.acceptRate
      const prev = rate && rate.prev
      if (!prev || prev.available === false) return null
      return typeof prev.percent === 'number' ? prev.percent : null
    },
    // detail of the "strong" stage: { designator, manual, ba_auto, legacy_notified_only }
    strongSplit () {
      if (!this.funnel) return null
      const stage = this.funnel.stages.find(row => row.key === 'strong')
      const detail = stage && stage.detail
      if (!detail || typeof detail.manual !== 'number' || typeof detail.ba_auto !== 'number') return null
      return detail
    },
    twinsToBa () {
      return this.funnel && this.funnel.sub_metrics
        ? this.funnel.sub_metrics.twins_to_ba
        : null
    },
    // Bars are scaled to the widest stage, not to the first one: the BA
    // breakdown and the conversation stages can legitimately be smaller by an
    // order of magnitude, and scaling to the max keeps them visible.
    maxCount () {
      if (!this.funnel) return 0
      const counts = this.funnel.stages
        .map(stage => stage.count)
        .filter(count => typeof count === 'number')
      return counts.length ? Math.max.apply(null, counts) : 0
    },
    firstCount () {
      if (!this.funnel || !this.funnel.stages.length) return 0
      const first = this.funnel.stages[0]
      return typeof first.count === 'number' ? first.count : 0
    },
    // The previous period's denominator — the first stage's prev_count. Zero
    // (or absent) means the previous period has no baseline to compare against,
    // and every parenthesised percentage disappears rather than dividing by it.
    prevFirstCount () {
      if (!this.funnel || !this.funnel.stages.length) return 0
      const first = this.funnel.stages[0]
      return typeof first.prev_count === 'number' ? first.prev_count : 0
    },
    pitchedApplicants () {
      if (!this.pitched || !Array.isArray(this.pitched.applicants)) return []
      const rows = this.pitched.applicants
      if (!this.bucket_filter) return rows
      // A card whose every offer was filtered out is dropped entirely — an empty
      // card would read as "this candidate got nothing".
      return rows.filter(row => this.offersOf(row).length > 0)
    },
    // Header figures for the drilldown, counted from the rows actually rendered
    // rather than taken from the stage above — the two are the same number when
    // both services agree, and when they disagree the list must describe itself.
    pitchedTotals () {
      const rows = this.pitchedApplicants
      if (!rows.length) return null
      const offers = rows.reduce((sum, row) => sum + this.offersOf(row).length, 0)
      return { applicants: rows.length, offers: offers }
    },
    // The backend decides which groups exist (green/orange/red always, "לא מזוהה"
    // only when it caught something), so the page renders what it is given rather
    // than owning that rule a second time.
    baGroups () {
      if (!this.ba_breakdown || !Array.isArray(this.ba_breakdown.groups)) return []
      return this.ba_breakdown.groups
    },
    baTotal () {
      const total = this.ba_breakdown && this.ba_breakdown.total
      return typeof total === 'number' ? total : null
    },
    // ---- 8->9 drop-off strip ---------------------------------------------
    dropOff () {
      return this.funnel && this.funnel.ba_dropoff ? this.funnel.ba_dropoff : null
    },
    // Which strip to draw, or null for "draw nothing". Four outcomes:
    //   clickable  — a real figure with a modal behind it
    //   static     — the server declined to compute it AND says why out loud
    //                (available:false, or too_many)
    //   null       — truncated / no_expected / the key is absent entirely
    // Only `clickable` is focusable; a static line carries no role, no tabindex
    // and no pointer cursor, so it cannot promise an interaction it has not got.
    dropOffVariant () {
      const strip = this.dropOff
      if (!strip) return null
      if (strip.available === false) return 'static'
      if (strip.hidden === true) {
        // 'rows_missing' is said out loud for the same reason 'too_many' is: the
        // figure is absent because nothing was measured, not because nothing
        // dropped. Only 'truncated' and 'no_expected' stay silent.
        return ['too_many', 'rows_missing'].indexOf(strip.hidden_reason) !== -1 ? 'static' : null
      }
      return typeof strip.dropped === 'number' && strip.label_he ? 'clickable' : null
    },
    dropOffStaticText () {
      const strip = this.dropOff
      if (!strip) return ''
      if (strip.available === false) {
        return 'נשרו בדרך ל-BA: אין נתון — שירות השיחות אינו זמין (' +
          this.reasonLabel(strip.reason) +
          '), ולכן אי אפשר לדעת אילו הצעות היו אמורות להגיע ל-BA. ' +
          'הנתון יחזור כששירות השיחות יחזור. אפשר לרענן את העמוד בעוד כמה דקות.'
      }
      if (strip.hidden_reason === 'rows_missing') return DROPOFF_ROWS_MISSING_TEXT
      return DROPOFF_TOO_MANY_TEXT
    },
    // The store is the only reliable signal that the modal closed:
    // ChatbotConversationModal never closes itself and emits nothing, and
    // Modal.vue's X removes the payload directly. Watching the list covers both
    // the X and our own Esc path.
    dropoffModalOpen () {
      const modals = this.$store.state.modals && this.$store.state.modals.modals
      return Array.isArray(modals) &&
        modals.some(modal => modal && modal.designator === 'funnel-dropoff-modal')
    },
    // ---- chip filter ------------------------------------------------------
    // Built from the SERVER's segment list, so a chip count can never disagree
    // with the legend it sits under. Suppressed when the row set is partial.
    bucketChips () {
      const pitched = this.pitched
      if (!pitched || pitched.available === false) return []
      if (pitched.truncated === true) return []
      const segments = Array.isArray(pitched.segments) ? pitched.segments : []
      if (!segments.length) return []
      const chips = segments
        .filter(segment => segment.count > 0 && CHIP_LABELS[segment.key])
        .map(segment => ({
          key: segment.key,
          label: CHIP_LABELS[segment.key],
          count: segment.count
        }))
      if (!chips.length) return []
      const total = typeof pitched.segments_total === 'number'
        ? pitched.segments_total
        : chips.reduce((sum, chip) => sum + chip.count, 0)
      // key 'all' is the v-for key (Vue needs a real one); chipBucket() maps it
      // back to the null the filter actually uses.
      return [{ key: CHIP_ALL_KEY, label: CHIP_ALL_LABEL, count: total }].concat(chips)
    },
    activeChipLabel () {
      const chip = this.bucketChips.find(row => this.chipBucket(row) === this.bucket_filter)
      return chip ? chip.label : CHIP_ALL_LABEL
    },
    // A filter that hides every card: the empty state has to name the filter and
    // offer the way out, never look like "there is no data".
    bucketFilterEmpty () {
      return !!this.bucket_filter && this.pitchedApplicants.length === 0
    },
    ...mapGetters('modals', ['findModal'])
  },
  watch: {
    // Focus returns to the strip on close — Esc and X alike.
    dropoffModalOpen (open, was) {
      if (!was || open) return
      this.$nextTick(() => this.focusDropoffStrip())
    }
  },
  created () {
    this.fetchFunnel()
  },
  mounted () {
    this.startTimer()
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy () {
    this.stopTimer()
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    fetchFunnel (isManual) {
      // One request in flight at a time. A background trigger that lands while
      // another is running (the poll firing on a slow response, or
      // visibilitychange arriving right after created()) is dropped; a MANUAL
      // refresh always goes, because the user asked for it.
      if (this.is_loading && !isManual) return
      this.is_loading = true
      // A MANUAL refresh bypasses the server's 60s shared conv-svc row memo; the
      // background poll rides on it, which is the whole point of the memo.
      const params = isManual ? { hours: 24, force: 1 } : { hours: 24 }
      this.$network_utils
        .getPipelineFunnel(params)
        .then(res => {
          if (!res || !res.stages) {
            this.error_text = 'התקבלה תשובה לא צפויה מהשרת'
            return
          }
          this.funnel = res
          this.error_text = ''
        })
        .catch(() => {
          this.error_text = 'שגיאה בטעינת המשפך'
          if (isManual) {
            this.$utils.emit('show_toast', { text: 'שגיאה בטעינת המשפך', type: 'error' })
          }
        })
        .finally(() => {
          this.is_loading = false
        })
    },
    // Auto-refresh only while the tab is actually being looked at, so a page
    // left open overnight does not poll the admin API 1440 times.
    startTimer () {
      this.stopTimer()
      this.timer = setInterval(() => {
        if (!document.hidden) this.fetchFunnel()
      }, REFRESH_MS)
    },
    stopTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    onVisibilityChange () {
      if (document.hidden) return
      this.fetchFunnel()
      this.startTimer()
    },
    barWidth (stage) {
      if (stage.count === null || !this.maxCount) return 0
      // Floor at 1.5% so a stage with a real but tiny count is still visible.
      return Math.max(1.5, (stage.count / this.maxCount) * 100)
    },
    // Divides by segments_total, NOT by count: the row-derived split and the
    // counter headline are two different queries, and a 79-row split over a
    // 74-count headline would overflow the track. segments_total is absent on an
    // older backend, so count is the fallback.
    segmentWidth (stage, segment) {
      const total = typeof stage.segments_total === 'number' && stage.segments_total > 0
        ? stage.segments_total
        : stage.count
      if (!total) return 0
      return (segment.count / total) * 100
    },
    // Pitched offers still waiting for a yes/no. Published by the backend as
    // detail.undecided, and only there — the BA breakdown's detail carries
    // `unknown` instead, so it is not affected by this. Zero is not rendered:
    // "ללא הכרעה 0" is noise, an absent chip says the same thing.
    //
    // Suppressed entirely once the SEGMENT list carries the same bucket: the
    // counter path now publishes `undecided` as a real segment (so the bar fills
    // to segments_total), and the legend above would otherwise print the figure
    // twice.
    undecided (stage) {
      const detail = stage && stage.detail
      if (!detail || typeof detail.undecided !== 'number' || detail.undecided <= 0) return null
      const segments = Array.isArray(stage.segments) ? stage.segments : []
      if (segments.some(segment => segment.key === 'undecided' || segment.key === 'ours_undecided')) {
        return null
      }
      return detail.undecided
    },
    // Distinct candidates behind the pitched stage's offers, published by the
    // backend as detail.unique_applicants (conv-svc `applicants_pitched`). Absent
    // when the deployed conversation service predates the field — then no line is
    // drawn at all, rather than a 0 that would read as "nobody was pitched".
    // typeof, never truthiness: an ABSENT key renders no line, a real 0 renders.
    // The "stage counted nothing" case is decided SERVER-side (the rebase stages
    // omit both keys on an empty window), so there is no count-based rule here —
    // one that existed would also stop stageNote() stripping the clause the note
    // still carries, and the figure would reappear inside the prose.
    uniqueApplicants (stage) {
      const detail = stage && stage.detail
      if (!detail || typeof detail.unique_applicants !== 'number') return null
      return detail.unique_applicants
    },
    // Stage 2 only (326 events over 323 candidacies): the gap is the signal.
    uniqueOffers (stage) {
      const detail = stage && stage.detail
      if (!detail || typeof detail.unique_offers !== 'number') return null
      return detail.unique_offers
    },
    peopleLabel (stage) {
      return PEOPLE_LABELS[stage.key] || PEOPLE_LABEL_FALLBACK
    },
    // ---- BA failing-reason lines (identical in BOTH drilldowns) ------------
    // Order: the server's failing dimensions when it published any, the legacy
    // flat paragraph when it did not, and nothing at all for a green verdict —
    // a green offer's "why it fits" is noise on a page about leaks.
    //
    // `group` is the BA drilldown's verdict group (the decision lives there, not
    // on the row); the pitched drilldown passes nothing and the decision is read
    // off the row's own ba object.
    baLines (offer, group) {
      if (!offer) return []
      const ba = group ? offer : offer.ba
      if (!ba) return []
      const decision = group ? group.key : ba.decision
      if (decision === 'green') return []
      const breakdown = ba.breakdown
      if (Array.isArray(breakdown) && breakdown.length) {
        return breakdown
          .filter(entry => entry && entry.label && entry.text)
          .map(entry => entry.label + ': ' + entry.text)
      }
      return ba.reasoning ? [ba.reasoning] : []
    },
    // No tooltip on any verdict. For red/orange the reasoning is now VISIBLE as
    // stacked lines, and two copies of one sentence (one of them hidden) is not a
    // feature; for green the flat text is the "why it fits" prose this page
    // deliberately does not show.
    baTagTitle () {
      return null
    },
    // The stage note, minus every clause this page already renders as its own
    // line (the people figure, and the partial-data caution).
    stageNote (stage) {
      if (!stage.note) return ''
      return String(stage.note)
        .split(NOTE_SEPARATOR)
        .filter(clause => !this.isOwnLineClause(stage, clause))
        .join(NOTE_SEPARATOR)
    },
    isOwnLineClause (stage, clause) {
      if (this.uniqueApplicants(stage) !== null && clause.indexOf(UNIQUE_APPLICANTS_LABEL) === 0) {
        return true
      }
      return clause.indexOf(PARTIAL_NOTE_PREFIX) === 0
    },
    // The "partial data" clause, promoted out of the prose so it reads as a
    // caution rather than as more explanation. Only when the server actually
    // flagged the row set as truncated.
    partialNote (stage) {
      if (!stage || stage.truncated !== true || !stage.note) return ''
      const clause = String(stage.note)
        .split(NOTE_SEPARATOR)
        .find(row => row.indexOf(PARTIAL_NOTE_PREFIX) === 0)
      return clause || ''
    },
    pctOfFirst (stage) {
      if (stage.count === null || !this.firstCount) return null
      return Math.round((stage.count / this.firstCount) * 1000) / 10
    },
    // The same ratio over the previous equivalent period — same rounding, so the
    // two numbers printed side by side are directly comparable and neither is
    // more precise than the other.
    //
    // Null (not 0) whenever the comparison cannot be made: this stage has no
    // previous count, or the previous period has no first stage to divide by.
    // The template renders no parentheses at all in that case — "0%" would read
    // as "collapsed to nothing", which is the opposite of "we don't know".
    prevPctOfFirst (stage) {
      if (typeof stage.prev_count !== 'number' || !this.prevFirstCount) return null
      return Math.round((stage.prev_count / this.prevFirstCount) * 1000) / 10
    },
    // Tooltip for the stage count: the previous period's raw number. Empty
    // string when there is none, which leaves the element with no title at all.
    prevCountTitle (stage) {
      if (typeof stage.prev_count !== 'number') return ''
      return 'התקופה הקודמת: ' + this.formatNumber(stage.prev_count)
    },
    // A server reason slug in the reader's language. conv_svc_http_503 and
    // friends share one phrase — the status code itself is for the log, not the
    // page.
    reasonLabel (reason) {
      const slug = String(reason || '').trim()
      if (!slug) return 'לא צוינה סיבה'
      if (REASON_LABELS[slug]) return REASON_LABELS[slug]
      if (slug.indexOf('conv_svc_http_') === 0) return 'שירות השיחות החזיר שגיאה'
      return slug
    },
    formatNumber (value) {
      return typeof value === 'number' ? value.toLocaleString('he-IL') : value
    },
    formatStamp (iso) {
      if (!iso) return ''
      const date = new Date(iso)
      return isNaN(date.getTime()) ? iso : date.toLocaleString('he-IL')
    },
    formatShortStamp (iso) {
      if (!iso) return ''
      const stamp = this.$moment(iso)
      return stamp.isValid() ? stamp.format('DD/MM HH:mm') : ''
    },

    // ---- drilldown -------------------------------------------------------
    isDrillStage (stage) {
      return DRILL_STAGE_KEYS.indexOf(stage.key) !== -1
    },
    isPitchedStage (stage) {
      return stage.key === PITCHED_STAGE_KEY
    },
    isBaStage (stage) {
      return stage.key === BA_STAGE_KEY
    },
    isDrilldownOpen (stage) {
      return this.open_drilldown === stage.key
    },
    drilldownId (stage) {
      return stage.key + '-drilldown'
    },
    toggleDrilldown (stage) {
      if (!this.isDrillStage(stage)) return
      // Clicking the open one closes it; clicking the other one switches.
      this.open_drilldown = this.isDrilldownOpen(stage) ? null : stage.key
      if (!this.isDrilldownOpen(stage)) return
      // Fetched on first open only. A stale list is refreshed by the button
      // inside the drilldown, never by the page's background poll.
      if (this.isPitchedStage(stage) && !this.pitched && !this.drill_loading) {
        this.fetchPitched()
      }
      if (this.isBaStage(stage) && !this.ba_breakdown && !this.ba_loading) {
        this.fetchBaBreakdown()
      }
    },
    fetchBaBreakdown (isManual) {
      this.ba_loading = true
      this.$network_utils
        .getPipelineFunnelBaBreakdown({ hours: 24 })
        .then(res => {
          if (!res || !Array.isArray(res.groups)) {
            this.ba_error = 'התקבלה תשובה לא צפויה מהשרת'
            return
          }
          this.ba_breakdown = res
          this.ba_error = ''
          this.expanded_messages = {}
        })
        .catch(() => {
          this.ba_error = 'שגיאה בטעינת פירוט החלטות ה-BA'
          if (isManual) {
            this.$utils.emit('show_toast', { text: 'שגיאה בטעינת פירוט החלטות ה-BA', type: 'error' })
          }
        })
        .finally(() => {
          this.ba_loading = false
        })
    },
    baOffersOf (group) {
      return group && Array.isArray(group.offers) ? group.offers : []
    },
    baOfferKey (offer, index) {
      return 'ba-offer-' + (offer && offer.offer_id ? offer.offer_id : index)
    },

    // ---- offer details (mirrors components/admin/CompanyOffers.vue) --------
    // The offer sub-object, or null when the Stat's offer no longer resolves in
    // this DB — the row is still listed, it just has no offer-side detail.
    offerOf (row) {
      return row && row.offer ? row.offer : null
    },
    statusColor (status) {
      const color = status && status.color
      return STATUS_COLORS[color] || STATUS_FALLBACK_COLOR
    },
    // The compact chips the company-offers table shows as their own columns
    // (distance / owner / score). Absent values are simply omitted rather than
    // rendered as an empty chip.
    offerMeta (row) {
      const offer = this.offerOf(row)
      if (!offer) return []
      const chips = []
      if (typeof offer.distance === 'number') chips.push('מרחק ' + offer.distance + ' ק"מ')
      if (offer.owner) chips.push('אחראי: ' + offer.owner)
      if (typeof offer.score === 'number') chips.push('ציון ' + offer.score)
      return chips
    },
    offerDates (row) {
      const offer = this.offerOf(row)
      if (!offer) return []
      const lines = []
      if (offer.sent_at) lines.push('נשלח: ' + this.formatShortStamp(offer.sent_at))
      if (offer.applicant_submitted_at) {
        lines.push('הוגש: ' + this.formatShortStamp(offer.applicant_submitted_at))
      }
      if (offer.updated_at) lines.push('עודכן: ' + this.formatShortStamp(offer.updated_at))
      return lines
    },
    isLongMessage (text) {
      return String(text || '').length > MESSAGE_PREVIEW_CHARS
    },
    messageKey (row, field) {
      const id = row && row.offer_id ? row.offer_id : 'unknown'
      return id + ':' + field
    },
    isMessageExpanded (row, field) {
      return !!this.expanded_messages[this.messageKey(row, field)]
    },
    toggleMessage (row, field) {
      const key = this.messageKey(row, field)
      this.$set(this.expanded_messages, key, !this.expanded_messages[key])
    },
    // Folded by default so one chatty offer cannot make its row taller than the
    // rest of the group.
    visibleMessage (row, text, field) {
      const full = String(text || '')
      if (!this.isLongMessage(full)) return full
      if (this.isMessageExpanded(row, field || 'message')) return full
      return full.slice(0, MESSAGE_PREVIEW_CHARS) + '…'
    },
    // Same treatment Message.vue#getMessage gives an offer message before
    // rendering: MessageForm appends a <messsage-meta> block carrying a raw
    // Date string, and the display swaps that raw string for a readable stamp.
    // ($sanitize then drops the tags themselves, leaving the text.)
    displayMessage (text) {
      const full = String(text || '')
      const stamp = this.$utils.extractStringInsideHtmlTag(full, 'message-date')
      if (!stamp || !stamp.length) return full
      return full.replace(stamp, this.time_helper.makeTimeReadable(stamp) + ' ע"י ')
    },

    // ---- offer message edit (reuses the company-screen modal) --------------
    // MessageForm reads resource_data.{id,message,auto_message} and builds its
    // PUT from additional_data.endpoint + resource_data.id. auto_message is
    // passed through so a save round-trips it UNCHANGED — the form always
    // submits that field, so omitting it would blank the column.
    messageResourceData (row) {
      const offer = this.offerOf(row)
      return {
        id: offer.id,
        message: offer.message || '',
        auto_message: offer.auto_message || '',
        applicant: row.applicant || {},
        position: row.position || {}
      }
    },
    // $utils.getResourceFullName would do this for an Offer, but it assumes a
    // nested position.company and throws on a deleted position, so the title is
    // built here with the same shape and a safe fallback.
    offerModalTitle (row) {
      const offer = this.offerOf(row)
      const parts = [
        this.applicantName(row),
        row.position && row.position.title,
        row.company && row.company.name
      ].filter(Boolean)
      return 'Message - ' + (parts.length ? parts.join(' - ') : 'הצעה #' + offer.id)
    },
    openOfferMessageModal (row) {
      const offer = this.offerOf(row)
      if (!offer || !offer.id) return
      this.createModal({
        designator: 'message-modal',
        component_name: 'MessageForm',
        resource_data: this.messageResourceData(row),
        additional_data: OFFER_MESSAGE_ADDITIONAL_DATA,
        is_submitable: true,
        modal_title: this.offerModalTitle(row),
        // MessageForm exposes an onSubmit prop (fired on a successful save,
        // before the modal closes) and Modal.vue v-binds payload.props onto it.
        // Refetching is the only way to get the saved text back — the form
        // appends its own meta block, so echoing the typed value would drift.
        props: { onSubmit: () => this.fetchBaBreakdown() }
      })
    },
    // ---- chatbot conversation ---------------------------------------------
    // Used by BOTH drilldowns (the pitched applicant cards and the BA rows), so
    // the argument is any row carrying an applicant: a pitched card has no
    // `position` and its position_title simply comes out undefined.
    // Either handle is enough: the row's own conv-svc conversation id, or the
    // applicant id the modal can resolve a conversation from.
    canOpenChat (offer) {
      return !!(offer && (offer.conversation_id || (offer.applicant && offer.applicant.resource_id)))
    },
    openChatModal (offer) {
      if (!this.canOpenChat(offer)) return
      // `modals/create` is a plain push with no dedup, so a second candidate's
      // chat would stack a SECOND modal at the same position over the first —
      // the reader would have to Esc twice to get back. Same close-then-open
      // pattern LinkEntity/ButtonEvaluate use for their own singleton modals.
      this.closeOpenChatModal()
      this.createModal({
        designator: 'chatbot-conversation-modal',
        component_name: 'ChatbotConversationModal',
        modal_title: 'שיחה עם הצ\'אטבוט · ' + this.applicantName(offer),
        modal_width: 700,
        is_center_modal: true,
        resource_data: {
          conversation_id: offer.conversation_id || null,
          applicant: offer.applicant || null,
          position_title: offer.position && offer.position.title
        }
      })
    },
    // is_fixed has to be cleared first: the `remove` mutation no-ops while it is
    // set (store/modules/modals/mutations.js).
    closeOpenChatModal () {
      const open = this.findModal({ type: 'designator', value: 'chatbot-conversation-modal' })
      if (!open) return
      open.is_fixed = false
      this.removeModal(open)
    },
    ...mapMutations('modals', { createModal: 'create', removeModal: 'remove' }),
    fetchPitched (isManual) {
      this.drill_loading = true
      this.$network_utils
        .getPipelineFunnelPitched({ hours: 24 })
        .then(res => {
          if (!res || typeof res !== 'object') {
            this.drill_error = 'התקבלה תשובה לא צפויה מהשרת'
            return
          }
          this.pitched = res
          this.drill_error = ''
          // A refetch can retire the bucket that was filtered on.
          if (this.bucket_filter && !this.bucketChips.some(chip => this.chipBucket(chip) === this.bucket_filter)) {
            this.bucket_filter = null
          }
        })
        .catch(() => {
          this.drill_error = 'שגיאה בטעינת פירוט ההצעות'
          if (isManual) {
            this.$utils.emit('show_toast', { text: 'שגיאה בטעינת פירוט ההצעות', type: 'error' })
          }
        })
        .finally(() => {
          this.drill_loading = false
        })
    },
    offersOf (row) {
      const offers = row && Array.isArray(row.offers) ? row.offers : []
      if (!this.bucket_filter) return offers
      return offers.filter(offer => offer.bucket === this.bucket_filter)
    },
    // The card's UNFILTERED offer list, so the badge can say "3 מתוך 8 הצעות"
    // and the active filter stays legible at the row level too.
    allOffersOf (row) {
      return row && Array.isArray(row.offers) ? row.offers : []
    },
    offersCountLabel (row) {
      const shown = this.offersOf(row).length
      const total = this.allOffersOf(row).length
      if (!this.bucket_filter || shown === total) {
        return this.formatNumber(total) + ' הצעות'
      }
      return this.formatNumber(shown) + ' מתוך ' + this.formatNumber(total) + ' הצעות'
    },
    // The bucket a chip filters on: null for the "הכול" chip, whose v-for key is
    // the string 'all' because Vue needs a real key.
    chipBucket (chip) {
      if (!chip || chip.key === CHIP_ALL_KEY) return null
      return chip.key
    },
    setBucketFilter (key) {
      this.bucket_filter = this.bucket_filter === key ? null : key
    },
    isMutedSegment (segment) {
      if (!segment) return false
      // Server-driven; the key list is only a fallback for a rebase deployed
      // before the flag existed.
      if (typeof segment.muted === 'boolean') return segment.muted
      return MUTED_SEGMENT_KEYS.indexOf(segment.key) !== -1
    },
    legendSegments (stage) {
      const segments = stage && Array.isArray(stage.segments) ? stage.segments : []
      return segments.filter(segment => segment.count > 0)
    },
    // ---- drop-off strip ---------------------------------------------------
    focusDropoffStrip () {
      // ref lives inside a v-for, so Vue hands back an array.
      const ref = this.$refs.dropoffStrip
      const element = Array.isArray(ref) ? ref[0] : ref
      if (element && element.focus) element.focus()
    },
    openDropOffModal () {
      if (this.dropOffVariant !== 'clickable') return
      const strip = this.dropOff
      this.createModal({
        designator: 'funnel-dropoff-modal',
        component_name: 'FunnelDropOffModal',
        modal_title: 'נשרו בדרך ל-BA · 24 שעות',
        modal_width: 980,
        is_center_modal: true,
        resource_data: {
          hours: 24,
          // The strip's figures AS THEY WERE when the modal opened: the page
          // keeps polling, and the modal compares against these to know whether
          // to raise its stale banner.
          summary: { dropped: strip.dropped, expected: strip.expected }
        }
      })
    },
    applicantKey (row, index) {
      const applicant = row && row.applicant
      const id = applicant && applicant.resource_id ? applicant.resource_id : 'unknown'
      return 'pitched-' + id + '-' + index
    },
    offerKey (offer, index) {
      return 'offer-' + (offer && offer.offer_id ? offer.offer_id : index)
    },
    applicantName (row) {
      const applicant = row && row.applicant
      if (!applicant) return 'מועמד לא ידוע'
      const joined = [applicant.first_name, applicant.last_name].filter(Boolean).join(' ').trim()
      return applicant.full_name || joined || 'מועמד לא ידוע'
    },
    applicantPayload (row) {
      const applicant = row.applicant || {}
      const nameParts = String(applicant.full_name || '').split(' ')
      return {
        resource_type: 'Applicant',
        resource_id: applicant.resource_id,
        first_name: applicant.first_name || nameParts[0] || '',
        last_name: applicant.last_name || nameParts.slice(1).join(' ') || '',
        full_name: applicant.full_name || this.applicantName(row),
        phone_number: applicant.phone_number || ''
      }
    },
    // LinkEntity's Position branch reads payload.title, payload.id and
    // payload.company.resource_id (for the company-positions query), plus
    // payload.is_hot (IsHot flame) and payload.is_discrete (' (DISCRETE)'
    // suffix), so all of them are supplied. A row whose position was deleted
    // arrives with position:null and is rendered as plain text instead.
    //
    // Shared by BOTH drilldowns: the pitched rows and the BA rows publish the
    // same position/company/applicant shapes on purpose, so the link helpers are
    // written once.
    positionPayload (offer) {
      const position = offer && offer.position
      if (!position || !position.id) return null
      const companyId = offer.company && offer.company.resource_id
        ? offer.company.resource_id
        : position.company_id || null
      return {
        resource_type: 'Position',
        id: position.id,
        resource_id: position.id,
        title: position.title || ('משרה #' + offer.offer_id),
        is_hot: position.is_hot,
        is_discrete: position.is_discrete,
        company: { resource_id: companyId }
      }
    },
    companyPayload (offer) {
      const company = offer && offer.company
      if (!company || !company.resource_id) return null
      return {
        resource_type: 'Company',
        resource_id: company.resource_id,
        name: company.name || ('חברה #' + company.resource_id)
      }
    },
    // An unrecognised response value is treated as undecided rather than
    // rendered raw — a new value on the server must not paint an unstyled tag.
    responseKey (offer) {
      const response = offer && offer.response
      return RESPONSE_LABELS[response] ? response : 'undecided'
    },
    responseLabel (offer) {
      return RESPONSE_LABELS[this.responseKey(offer)]
    },
    baKey (offer) {
      const decision = offer && offer.ba && offer.ba.decision
      return BA_LABELS[decision] ? decision : 'unknown'
    },
    baLabel (offer) {
      return BA_LABELS[this.baKey(offer)]
    }
  }
}
</script>

<style scoped>
.pipeline-funnel-page {
  padding: 20px;
  text-align: right;
}
.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 6px;
  color: #2c3e50;
}
.page-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 18px;
  line-height: 1.6;
  max-width: 720px;
}
.refresh-btn {
  border: 1px solid #cbd5e0;
  background: #fff;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 14px;
  font-family: inherit;
  color: #2c3e50;
  cursor: pointer;
}
.refresh-btn:hover:not(:disabled) {
  background: #f7fafc;
}
.refresh-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.notice {
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 16px;
}
.notice-warning {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
}
.notice-error {
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #991b1b;
}
.notice-retry {
  margin-inline-start: 12px;
  border: 1px solid #fca5a5;
  background: #fff;
  border-radius: 6px;
  padding: 3px 12px;
  font-family: inherit;
  font-size: 12px;
  color: #991b1b;
  cursor: pointer;
}
.loading-wrap {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
.funnel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 18px;
}
.stage {
  padding: 14px 0;
  border-bottom: 1px solid #f1f5f9;
}
.stage:last-child {
  border-bottom: none;
}
.stage-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.stage-index {
  width: 22px;
  height: 22px;
  flex: none;
  border-radius: 50%;
  background: #eef2f7;
  color: #64748b;
  font-size: 12px;
  line-height: 22px;
  text-align: center;
}
.stage-label {
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
}
.stage-count {
  font-size: 15px;
  font-weight: 700;
  color: #1d4ed8;
}
.stage-pct {
  font-size: 12px;
  color: #6b7280;
}
/* The previous period's figure, wherever it appears (stage rows and the accept
   rate badge). Deliberately quieter than whatever it sits next to: it is
   context for the current number, never a number to read first. Inherits its
   size so it stays proportional in both places. */
.pct-prev {
  opacity: 0.65;
  font-weight: 400;
  white-space: nowrap;
}
.stage-empty {
  font-size: 12px;
  color: #94a3b8;
}
.bar-track {
  background: #f1f5f9;
  border-radius: 6px;
  height: 16px;
  overflow: hidden;
}
.bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 6px;
  transition: width 0.4s ease;
}
.bar-segmented {
  display: flex;
  background: none;
}
.bar-segment {
  height: 100%;
}
.seg-green {
  background: #16a34a;
}
.seg-orange {
  background: #f59e0b;
}
.seg-red {
  background: #dc2626;
}
/* Pitched-offer verdicts reuse the BA verdict tones: wanted reads as the same
   "good" green, declined as the same "bad" red, so one glance down the page
   means the same thing on every bar. */
.seg-wanted {
  background: #16a34a;
}
.seg-declined {
  background: #dc2626;
}
.seg-undecided {
  background: #cbd5e0;
}
/* The BA drilldown's "לא מזוהה" group dot. No bar segment uses it (the stage
   keeps `unknown` in detail, not in segments), but the group heading does. */
.seg-unknown {
  background: #cbd5e0;
}
.legend {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 8px;
  font-size: 12px;
  color: #475569;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.legend-muted {
  color: #94a3b8;
}
.legend-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}
.badge-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.badge {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  color: #1e3a8a;
}
.badge-sub {
  color: #475569;
  margin-inline-start: 4px;
}
.badge-note {
  font-size: 11px;
  color: #94a3b8;
}
.stage-note {
  margin-top: 6px;
  font-size: 11px;
  color: #94a3b8;
}
/* A note that carries a NUMBER, not a caveat: same note rhythm, one step darker
   so the figure is readable at a glance without competing with the stage count. */
.stage-note-figure {
  color: #64748b;
}
.stage-note-figure b {
  color: #334155;
}
.stamp {
  margin-top: 14px;
  font-size: 12px;
  color: #6b7280;
}

/* ---- pitched-offers drilldown --------------------------------------------
   Same palette and rhythm as the funnel above: the card is one step off-white
   inside the white panel, the response tags reuse the segment colours, and the
   chat bubbles reuse the chatbot screen's inbound/outbound tones. */
.stage-head-drillable {
  cursor: pointer;
  border-radius: 6px;
  padding: 2px 4px;
  margin-inline-start: -4px;
}
.stage-head-drillable:hover {
  background: #f8fafc;
}
.stage-head-drillable:focus-visible {
  outline: 2px solid #93c5fd;
  outline-offset: 2px;
}
.bar-track-drillable {
  cursor: pointer;
}
.drill-hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #1d4ed8;
}
.drill-chevron {
  font-style: normal;
  font-size: 13px;
  line-height: 1;
}
.drilldown {
  margin-top: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 14px;
}
.drilldown-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.drilldown-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.drilldown-title-sub {
  font-weight: 400;
  font-size: 12px;
  color: #6b7280;
  margin-inline-start: 6px;
}
.drill-refresh {
  border: 1px solid #cbd5e0;
  background: #fff;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-family: inherit;
  color: #2c3e50;
  cursor: pointer;
}
.drill-refresh:hover:not(:disabled) {
  background: #f1f5f9;
}
.drill-refresh:disabled {
  opacity: 0.6;
  cursor: default;
}
.drill-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.drill-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
}
.drill-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.drill-phone {
  font-size: 12px;
  color: #6b7280;
  direction: ltr;
}
/* The BA drilldown states the verdict once per group instead of once per row,
   so the group heading carries the same colour dot the bar above uses. */
.drill-group-label {
  font-size: 14px;
  font-weight: 700;
  color: #2c3e50;
}
/* ---- offer details cell, mirroring the company-offers display ------------- */
.drill-offer-cell {
  max-width: 320px;
}
/* .status_color_text is global (App.vue) and gives the badge its white bold
   text; this only sizes it down for a dense table cell. */
.drill-status {
  border-radius: 6px;
  font-size: 12px;
  padding: 3px 8px !important;
}
.drill-offer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.drill-offer-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
  font-size: 11px;
  color: #6b7280;
}
.drill-offer-head {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.drill-offer-id {
  font-size: 11px;
  color: #6b7280;
  direction: ltr;
  unicode-bidi: embed;
}
.drill-offer-message {
  margin-top: 6px;
}
/* The message is the click target for the shared edit modal, same as the
   company offers screen — so it has to look editable. */
.drill-offer-editable {
  cursor: pointer;
  border-radius: 6px;
  padding: 2px 4px;
  margin-inline-start: -4px;
}
.drill-offer-editable:hover {
  background: #f1f5f9;
}
.drill-edit-icon {
  opacity: 0.65;
}
.drill-offer-editable:hover .drill-edit-icon {
  opacity: 1;
}
/* Same presentation Message.vue gives offers.message (its .custom_message is
   scoped, so the rules are restated here rather than inherited). */
.drill-offer-message .custom_message {
  margin: 0;
  text-align: right;
  white-space: pre-line;
  font-size: 12px;
  line-height: 1.6;
  color: #374151;
  overflow-wrap: anywhere;
}
.drill-auto-message {
  color: #6b7280 !important;
  font-size: 11px !important;
}
.drill-more-btn {
  margin-top: 2px;
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 11px;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
}
.badge-count {
  background: #f1f5f9;
  border-color: #e2e8f0;
  color: #475569;
}
.badge-mini {
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  margin-inline-end: 4px;
  margin-bottom: 2px;
}
.drill-chat-btn {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 12px;
  font-family: inherit;
  color: #1e3a8a;
  cursor: pointer;
  margin-inline-start: auto;
}
.drill-chat-btn:hover {
  background: #dbeafe;
}
/* Inside a table cell the button sits UNDER the phone, not pushed to the far
   edge like the card-head variant (whose margin-inline-start:auto belongs to a
   flex row). */
.drill-chat-btn-row {
  display: inline-block;
  margin-top: 4px;
  margin-inline-start: 0;
}
.drill-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.drill-table th {
  text-align: right;
  font-weight: 600;
  color: #64748b;
  font-size: 11px;
  padding: 4px 6px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}
.drill-table td {
  padding: 6px;
  border-bottom: 1px solid #f8fafc;
  vertical-align: top;
  color: #334155;
}
.drill-table tr:last-child td {
  border-bottom: none;
}
.drill-plain {
  color: #94a3b8;
}
.drill-tag {
  display: inline-block;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11px;
  color: #fff;
  white-space: nowrap;
}
.drill-tag-wanted {
  background: #16a34a;
}
.drill-tag-declined {
  background: #dc2626;
}
.drill-tag-undecided {
  background: #cbd5e0;
  color: #475569;
}
/* BA verdict tags — same tones as the ba_breakdown bar segments. */
.drill-tag-ba-green {
  background: #16a34a;
}
.drill-tag-ba-orange {
  background: #f59e0b;
}
.drill-tag-ba-red {
  background: #dc2626;
}
.drill-tag-ba-unknown {
  background: #cbd5e0;
  color: #475569;
}
.drill-answered {
  font-size: 11px;
  color: #94a3b8;
  margin-inline-start: 6px;
  white-space: nowrap;
}
.drill-reason {
  margin-top: 3px;
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.5;
}

/* ══ Pipeline-funnel additions (P0-1..P0-4). Every selector below is NEW — no
      existing rule above is modified, and .seg-wanted / .seg-declined /
      .seg-undecided / .seg-unknown / .stage-note-figure / .drill-reason /
      .drill-chat-btn are reused as they are. ══════════════════════════════ */

/* ---- stage-8 buckets: one colour vocabulary, bar = legend dot = chip = tag -- */
.seg-self_applied {
  background: #64748b;   /* slate: "not our offer" — apart from the verdict tones */
}
.seg-ours_wanted {
  background: #16a34a;
}
.seg-ours_declined {
  background: #dc2626;
}
.seg-inherited {
  background: #94a3b8;
}
.seg-ours_undecided {
  background: #b6c2d0;   /* a touch darker than #cbd5e0 so it survives the muting */
}
/* ---- stage-7 machine check ---- */
.seg-passed {
  background: #16a34a;
}
.seg-filtered_out {
  background: #dc2626;   /* the red family already means "disqualified" here */
}
/* A grey segment on the #f1f5f9 track needs an edge to exist at all. */
.bar-segment.seg-unknown {
  box-shadow: inset 0 0 0 1px #b6c2d0;
}
/* Muted = countable, not alarming. Hatching + reduced opacity instead of a new
   colour, scoped to the BAR: the 9px legend dot stays solid (hatching is
   illegible at that size) and muted legend items step down in text colour only. */
.bar-segment.seg-muted {
  opacity: .55;
  box-shadow: inset 0 0 0 1px #b6c2d0;
  background-image: repeating-linear-gradient(135deg,
    rgba(255, 255, 255, .55) 0 3px, transparent 3px 6px);
}

/* A caveat, not a figure — so it must NOT get .stage-note-figure's darker ink. */
.stage-note-partial {
  color: #92400e;
}

/* ---- chip filter ---- */
.chip-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.chip {
  height: 26px;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 12px;
  font-weight: 400;
  font-family: inherit;
  color: #475569;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}
.chip:hover {
  background: #f8fafc;
  border-color: #cbd5e0;
}
.chip:active {
  background: #eef2f7;
}
.chip[aria-pressed="true"] {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1e40af;
  font-weight: 600;
}
.chip:disabled {
  opacity: .6;
  cursor: default;
}
.chip-inline {
  margin-inline-start: 8px;
}
.chip-dot {
  margin-inline-end: 5px;
}
.chip-count {
  margin-inline-start: 6px;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}
.chip[aria-pressed="true"] .chip-count {
  color: #1e40af;
}

/* ---- row bucket tags (light tags take dark text, like .drill-tag-undecided) -- */
.drill-tag-self_applied {
  background: #64748b;
}
.drill-tag-ours_wanted {
  background: #16a34a;
}
.drill-tag-ours_declined {
  background: #dc2626;
}
.drill-tag-inherited {
  background: #e2e8f0;
  color: #475569;
}
.drill-tag-ours_undecided {
  background: #cbd5e0;
  color: #475569;
}

/* ---- the 8->9 drop-off strip ----
   The page's ONLY dashed edge, and the only block with no stage number: it
   belongs to the gap between two stages, not to the list of stages. */
.dropoff-strip {
  margin: 6px 0 10px;
  padding: 10px 12px;
  border: 1px dashed #cbd5e0;
  border-radius: 8px;
  background: #f8fafc;
}
.dropoff-strip-clickable {
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}
.dropoff-strip-clickable:hover {
  background: #f1f5f9;
  border-color: #b6c2d0;
}
.dropoff-strip-clickable:active {
  background: #eef2f7;
}
.dropoff-strip-clickable:focus-visible {
  outline: 2px solid #93c5fd;
  outline-offset: 2px;
}
/* No chevron, no hover, no pointer: a static line must not promise a click. */
.dropoff-strip-static {
  background: #fff;
  border-color: #e2e8f0;
  cursor: default;
}
.dropoff-strip-static .dropoff-label {
  color: #64748b;
  font-weight: 400;
}
.dropoff-arrow {
  font-size: 12px;
  color: #94a3b8;
  margin-inline-end: 6px;
}
.dropoff-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.dropoff-footnote {
  margin-top: 3px;
}

/* ---- BA failing-reason lines (identical in BOTH drilldowns) ----
   Deliberately identical to .drill-reason: never bold, never coloured, label and
   text share one ink. The verdict tag stays the only coloured thing in the cell. */
.ba-line {
  margin: 3px 0 0;
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.ba-line:first-of-type {
  margin-top: 4px;
}
</style>
